import type { LlmModel, LlmModelSelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { LlmModelSelector } from '@components/shared/llm-model-selector.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { Textarea } from '@components/ui/textarea';
import { cn } from '@lib/utils';
import { llmModelStore } from '@stores/llm-model.store';
import { BookOpenIcon, Loader2Icon, MaximizeIcon, MinimizeIcon, SendIcon, XIcon } from 'lucide-react';
import React, { ReactNode } from 'react';
import { useStore } from 'zustand';
import { IconButton } from '../core/icon-button.component';

export type PromptGuidelines = {
  definition?: string;
  subtitle?: string;
  title: string;
};

// Form content component
type FormContentProps = {
  description?: ReactNode;
  information?: PromptGuidelines;
  handleExpand: () => void;
  handleClose?: () => void;
  handleSubmit: (e: React.FormEvent) => void;
  isDialog?: boolean;
  isLoading?: boolean;
  maxLength?: number;
  modelFilter?: (model: LlmModelSelectDto) => boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  prompt: string;
  setPrompt: (value: string) => void;
  stop?: () => void;
  title?: string;
};

const FormContent: React.FC<FormContentProps> = ({
  description,
  information,
  handleExpand,
  handleClose,
  handleSubmit,
  isDialog = false,
  isLoading,
  maxLength,
  modelFilter,
  onKeyDown,
  placeholder,
  prompt,
  setPrompt,
  stop,
  title,
}) => {
  const { subtitle, definition } = information || {};

  return (
    <div className={cn('flex flex-col items-stretch gap-2')}>
      {title && !isDialog && (
        <div className={cn('flex items-start justify-between gap-2 w-full', !isDialog && 'pt-1 px-1')}>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
            {subtitle && <div className="text-xs text-gray-700">{subtitle}</div>}
          </div>

          <div className="flex items-center gap-1">
            <IconButton
              onClick={handleExpand}
              size="md"
              type="button"
              variant="secondary"
              icon={<MaximizeIcon />}
            />
            {handleClose && (
              <IconButton
                onClick={handleClose}
                size="md"
                type="button"
                variant="secondary"
                icon={<XIcon />}
              />
            )}
          </div>
        </div>
      )}

      {definition && isDialog && (
        <Accordion
          type="single"
          collapsible
          className="border rounded-lg"
        >
          <AccordionItem
            value="definition"
            className="border-none"
          >
            <AccordionTrigger className="px-3 py-2 hover:no-underline text-sm font-medium text-gray-700">
              <div className="flex items-center gap-2">
                <BookOpenIcon size={16} />
                <span>Definition</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3 text-xs whitespace-pre-wrap text-gray-600 max-h-48 overflow-y-auto">
              {definition}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {description && (
        <span className={cn('px-1 text-left py-1.5 text-gray-600 text-xs', isDialog && 'text-sm')}>{description}</span>
      )}

      <form
        className="w-full rounded bg-gray-100 border border-gray-200 p-1 focus-within:border-gray-500"
        onSubmit={handleSubmit}
      >
        <Textarea
          name="prompt"
          className={cn('border-none outline-none ring-0! resize-y p-1 bg-transparent')}
          disabled={isLoading}
          maxLength={maxLength}
          onKeyDown={onKeyDown}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={placeholder}
          rows={6}
          value={prompt}
        />

        <div className="flex items-center justify-end gap-1.5">
          <LlmModelSelector modelFilter={modelFilter} />
          {isLoading && stop ? (
            <Button
              onClick={() => stop()}
              size="xs"
              type="button"
              variant="secondary"
            >
              <Loader2Icon className="animate-spin" />
              <span>Stop</span>
            </Button>
          ) : (
            <Button
              disabled={isLoading}
              size="xs"
              type="submit"
              variant="primary"
            >
              <SendIcon />
              <span>Send</span>
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export type PromptPopoverProps = {
  className?: string;
  description?: ReactNode;
  information?: PromptGuidelines;
  isLoading?: boolean;
  isOpen?: boolean;
  maxLength?: number;
  modelFilter?: (model: LlmModelSelectDto) => boolean;
  /**
   * When provided, the Maximize action delegates to the consumer (e.g. opening the
   * LlmChatModal workspace) instead of the built-in single-column fullscreen modal.
   */
  onExpand?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onOpenChange?: (open: boolean) => void;
  onSubmit: (data: { model?: LlmModel; prompt?: string }) => void;
  placeholder?: string;
  prompt?: string;
  setPrompt?: (prompt: string) => void;
  title?: string;
  trigger?: React.ReactNode;
  stop?: () => void;
};

export const PromptPopover: React.FC<PromptPopoverProps> = (props) => {
  const {
    className,
    description,
    information,
    isLoading = false,
    isOpen,
    maxLength,
    modelFilter,
    onExpand,
    onKeyDown,
    onOpenChange,
    onSubmit,
    placeholder = '(Optional) Additional context or prompts',
    prompt: parentPrompt,
    setPrompt: parentSetPrompt,
    title,
    trigger,
    stop,
  } = props;
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [internalPrompt, setInternalPrompt] = React.useState('');
  const { selectedModel } = useStore(llmModelStore);

  const isPopoverOpen = isOpen !== undefined ? isOpen : internalIsOpen;
  const setIsPopoverOpen = onOpenChange || setInternalIsOpen;

  const prompt = parentPrompt || internalPrompt;
  const setPrompt = parentSetPrompt || setInternalPrompt;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const submitData: { model?: LlmModel; prompt?: string } = {
      model: selectedModel,
      prompt: prompt.trim(),
    };

    onSubmit(submitData);
    setIsPopoverOpen(false);
    setIsFullscreen(false);
    setPrompt('');
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
    setIsFullscreen(false);
    setPrompt('');
  };

  const handleMinimize = () => {
    setIsFullscreen(false);
  };

  const handleExpand = () => {
    if (onExpand) {
      setIsPopoverOpen(false);
      onExpand();
      return;
    }

    setIsFullscreen(true);
  };

  if (isFullscreen) {
    return (
      <Modal
        centered
        isOpen={isFullscreen}
        onClose={handleClose}
        width="80%"
        maxWidth="800px"
        header={{
          actions: (
            <IconButton
              onClick={handleMinimize}
              size="md"
              type="button"
              variant="secondary"
              icon={<MinimizeIcon />}
            />
          ),
          className: 'text-gray-800',
          subtitle: information?.subtitle ? <span className="text-gray-700">{information?.subtitle}</span> : undefined,
          title: title || 'Generate Content',
        }}
      >
        <FormContent
          handleExpand={handleExpand}
          handleSubmit={handleSubmit}
          description={description}
          information={information}
          isDialog
          isLoading={isLoading}
          maxLength={maxLength}
          modelFilter={modelFilter}
          onKeyDown={onKeyDown}
          setPrompt={setPrompt}
          placeholder={placeholder}
          prompt={prompt}
          stop={stop}
          title={title}
        />
      </Modal>
    );
  }

  return (
    <Popover
      onOpenChange={(open) => {
        setIsPopoverOpen(open);
      }}
      open={isPopoverOpen}
    >
      {stop && isLoading ? (
        <Button
          className={cn(
            'hover:text-[#3D2EFF] z-10',
            isPopoverOpen || isLoading ? 'text-[#3D2EFF] bg-[#e0e0f7]' : '',
            className,
          )}
          onClick={() => stop()}
          size="xs"
          variant="secondary"
        >
          <span className="animate-pulse flex items-center gap-1">
            <Loader2Icon className="animate-spin" />
            Stop
          </span>
        </Button>
      ) : (
        trigger && <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}

      <PopoverContent
        align="start"
        className={cn('p-1 w-80', !isPopoverOpen && 'invisible', className)}
        side="right"
      >
        <FormContent
          description={description}
          information={information}
          handleExpand={handleExpand}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          isDialog={false}
          isLoading={isLoading}
          maxLength={maxLength}
          modelFilter={modelFilter}
          onKeyDown={onKeyDown}
          setPrompt={setPrompt}
          placeholder={placeholder}
          prompt={prompt}
          stop={stop}
          title={title}
        />
      </PopoverContent>
    </Popover>
  );
};
